import React, { useState, useEffect, useContext, Fragment } from "react";
import SessionContext from "../../Contexts/SessionContext";
import { getBackendUrl } from "../../config";

const PageNotepad = () => {
  const { sessionId } = useContext(SessionContext);
  const [reports, setReports] = useState([]);
  const [words, setWords] = useState([]);
  const [wrongQuestionDetails, setWrongQuestionDetails] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const backendUrl = getBackendUrl();

  useEffect(() => {
    if (!sessionId) return;

    const token = localStorage.getItem("jstoken");
    const headers = {
      Authorization: token ? `Bearer ${token}` : undefined,
      "x-session-id": sessionId,
      "Content-Type": "application/json",
    };

    const fetchData = async () => {
      try {
        // Fetch dashboard, dictionary, and notes in parallel
        const [dashboardRes, dictionaryRes, notesRes, mistakesRes] =
          await Promise.all([
            fetch(`${backendUrl}/dashboard/user/`, { headers }),
            fetch(`${backendUrl}/dictionnary/user/`, { headers }),
            fetch(`${backendUrl}/notepad/user/`, { headers }),
            fetch(`${backendUrl}/mistakeQuestion/user`, { headers }),
          ]);

        const dashboardData = await dashboardRes.json();
        if (dashboardData.dashboard) setReports(dashboardData.dashboard);

        const dictionaryData = await dictionaryRes.json();
        if (dictionaryData.dictionnary) setWords(dictionaryData.dictionnary);

        const notesData = await notesRes.json();
        console.log("Notes response data:", notesData);
        console.log("Notes array:", notesData.notepad);
        if (notesData.notepad) setNotes(notesData.notepad);

        const mistakesData = await mistakesRes.json();
        if (
          mistakesData.mistakeQuestions &&
          mistakesData.mistakeQuestions.length > 0
        ) {
          const questionIds = mistakesData.mistakeQuestions
            .flatMap((q) => q.questions_wrong_id)
            .join(",");
          if (questionIds) {
            const questionsRes = await fetch(
              `${backendUrl}/questions/notepad`,
              {
                method: "POST",
                body: JSON.stringify({ question_ids: questionIds }),
                headers: headers,
              }
            );
            const questionsData = await questionsRes.json();
            if (questionsData.questions) {
              // Combine mistake session info with question details
              const detailedMistakes = mistakesData.mistakeQuestions.map(
                (mistake) => ({
                  ...mistake,
                  questions: questionsData.questions.filter((q) =>
                    mistake.questions_wrong_id.includes(q.question_id)
                  ),
                })
              );
              setWrongQuestionDetails(detailedMistakes);
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch notepad data:", error);
      }
    };

    fetchData();
  }, [sessionId, backendUrl]);

  const updateNote = async (e) => {
    e.preventDefault();
    const note_id = e.target.elements.note_id.value;
    const note = e.target.elements.note_content.value;
    const token = localStorage.getItem("jstoken");

    try {
      const res = await fetch(`${backendUrl}/notepad/${note_id}`, {
        method: "PUT",
        body: JSON.stringify({ note }),
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
          "x-session-id": sessionId,
        },
      });

      if (!res.ok) throw new Error("Failed to update note");

      const updatedNotes = notes.map((n) =>
        n.note_id.toString() === note_id ? { ...n, note: note } : n
      );
      setNotes(updatedNotes);
    } catch (err) {
      console.error("Error updating note:", err);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardTab reports={reports} />;
      case "notes":
        return <NotesTab notes={notes} onUpdateNote={updateNote} />;
      case "dictionary":
        return <DictionaryTab words={words} />;
      case "mistakes":
        return <MistakesTab mistakes={wrongQuestionDetails} />;
      default:
        return null;
    }
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">Notepad</h2>
      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {['dashboard', 'notes', 'dictionary', 'mistakes'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`${
                activeTab === tab
                  ? "border-indigo-500 text-indigo-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>
      <div>{renderContent()}</div>
    </div>
  );
};

// Sub-components for each tab for better organization

const DashboardTab = ({ reports }) => (
  <div className="overflow-x-auto bg-white p-4 rounded-lg shadow">
    <table className="min-w-full border">
      <thead className="bg-gray-100">
        <tr>
          <th className="py-3 px-4 border-b text-left text-sm font-semibold text-gray-600">
            Session
          </th>
          <th className="py-3 px-4 border-b text-left text-sm font-semibold text-gray-600">
            Result
          </th>
        </tr>
      </thead>
      <tbody>
        {reports.map((report, index) => (
          <tr key={index} className="hover:bg-gray-50">
            <td className="py-3 px-4 border-b border-gray-200">
              {report.session_name}
            </td>
            <td className="py-3 px-4 border-b border-gray-200">
              <p>
                Score:{" "}
                {((report.nb_good / report.total_question) * 100).toFixed(2)}%
              </p>
              <p className="text-xs text-gray-500">
                Time Used:{" "}
                {(
                  100 -
                  (report.time_remaining /
                    (report.time_per_question * report.total_question)) *
                    100
                ).toFixed(2)}
                %
              </p>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NotesTab = ({ notes, onUpdateNote }) => (
  <div className="space-y-4">
    {notes.map((note) => (
      <form
        key={note.note_id}
        onSubmit={onUpdateNote}
        className="p-4 border rounded-lg bg-white shadow"
      >
        <h3 className="font-bold text-gray-800">
          Session: {note.session_name}
        </h3>
        <input type="hidden" name="note_id" value={note.note_id} />
        <textarea
          name="note_content"
          defaultValue={note.note}
          className="w-full p-2 border rounded mt-2 h-24"
        />
        <button
          type="submit"
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Update Note
        </button>
      </form>
    ))}
  </div>
);

const DictionaryTab = ({ words }) => (
  <div className="overflow-x-auto bg-white p-4 rounded-lg shadow">
    <table className="min-w-full border">
      <thead className="bg-gray-100">
        <tr>
          <th className="py-3 px-4 border-b text-left text-sm font-semibold text-gray-600">
            Session
          </th>
          <th className="py-3 px-4 border-b text-left text-sm font-semibold text-gray-600">
            Word
          </th>
        </tr>
      </thead>
      <tbody>
        {words.map((word, i) => (
          <tr key={i} className="hover:bg-gray-50">
            <td className="py-3 px-4 border-b border-gray-200">
              {word.session_name}
            </td>
            <td className="py-3 px-4 border-b border-gray-200">{word.word}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MistakesTab = ({ mistakes }) => (
  <div className="space-y-4">
    {mistakes.map((mistake, index) => (
      <div key={index} className="p-4 border rounded-lg bg-white shadow">
        <h3 className="font-bold text-gray-800">
          Session: {mistake.session_name}
        </h3>
        <div className="mt-2 space-y-2">
          {mistake.questions.map((question, ind) => (
            <div key={ind} className="p-2 border-t">
              <p className="font-semibold text-gray-700">
                {question.question_title}
              </p>
              <div className="text-sm text-gray-600 pl-4">
                <p>a: {question.answer_title_a}</p>
                <p>b: {question.answer_title_b}</p>
                <p>c: {question.answer_title_c}</p>
                <p>d: {question.answer_title_d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default PageNotepad;
